import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from '@spartacus/asm/root';
import { CmsService, Product, WindowRef } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { CookieService } from 'ngx-cookie';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { StorageService } from 'src/app/service/storage/storage.service';

@Component({
  selector: 'app-cookie-product-capture',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookieProductCaptureComponent implements OnInit, OnDestroy {
  subscription: Subscription;
  productCode: string;
  currentUID: string;
  asmEnabled = this.winRef.localStorage.getItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  cookieSubscribption$: Subscription;
  recentlyViewCookie$: Subscription;

  // Sample:
  asmUserId: string;
  currentDealerCookieValue: any;
  currentSalesRepCookieValue: any;
  recentlyViewedCookieKey: string;
  constructor(
    protected cookieService: CookieService,
    private currentProductService: CurrentProductService,
    private cd: ChangeDetectorRef,
    protected cms: CmsService,
    private storageService: StorageService,
    private winRef: WindowRef
  ) {}

  product$: Observable<Product> = this.currentProductService.getProduct();

  ngOnInit() {
    this.subscription = this.currentProductService
      .getProduct()
      .pipe(filter(Boolean))
      .subscribe((product: Product) => {
        this.productCode = product.code;
        this.setProductToCookie(this.productCode);
        this.cd.markForCheck();
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.recentlyViewCookie$) {
      this.recentlyViewCookie$.unsubscribe();
    }
  }

  setProductToCookie(code: string) {
    this.currentUID = this.storageService.getSessionStorageItem('recentlyViewedCookieKey');
    const recentlyViewedCookieName = this.currentUID;
    const MAX_SKU_LIMIT = 12; // SKU MAX LIMIT

    if (!this.getCookie(recentlyViewedCookieName)) {
      this.updateRecentlyViewedCookie(recentlyViewedCookieName, code, null, MAX_SKU_LIMIT);
    } else {
      if (!this.getCookie(recentlyViewedCookieName).includes(code)) {
        const existingCodes = this.getCookie(recentlyViewedCookieName);
        this.updateRecentlyViewedCookie(recentlyViewedCookieName, code, existingCodes, MAX_SKU_LIMIT);
      } else {
        this.cms
          .getCurrentPage()
          .pipe(filter(Boolean), take(1))
          .subscribe((page) => {
            if (page && page.template === 'ProductDetailsPageTemplate') {
              const recentlyViewedProduct = this.getCookie(recentlyViewedCookieName);
              const result = recentlyViewedProduct;
              if (result) {
                const existingCodes = result;
                this.updateRecentlyViewedCookie(recentlyViewedCookieName, code, existingCodes, MAX_SKU_LIMIT);
              }
            }
          });
      }
    }
  }

  manageRecentlyViewedCookies(latestCookieName: string, maxCookies: number = 5) {
    // Get all recentlyViewedProduct_* cookies
    const allCookies = document.cookie
      .split(';')
      .map((c) => c.trim())
      .filter((c) => c.startsWith('recentlyViewedProduct_'))
      .map((c) => c.split('='))
      .map(([name, value]) => {
        try {
          const parsed = JSON.parse(decodeURIComponent(value));
          return { name, updatedAt: parsed.updatedAt || 0 };
        } catch {
          // fallback if old format or invalid JSON
          return { name, updatedAt: 0 };
        }
      });

    // Check if latestCookieName already exists
    const exists = allCookies.some((c) => decodeURIComponent(c.name) === decodeURIComponent(latestCookieName));

    // Remove oldest only if key is new and total exceeds maxCookies
    if (!exists && allCookies.length >= maxCookies) {
      // sort by updatedAt ascending (oldest first)
      allCookies.sort((a, b) => a.updatedAt - b.updatedAt);
      const oldest = allCookies[0];

      // remove the oldest cookie safely
      const decodedName = decodeURIComponent(oldest.name);
      this.cookieService.remove(decodedName, { path: '/' });
    }
  }

  updateRecentlyViewedCookie(
    cookieName: string,
    newValue: string,
    existingCookie: string | string[] | null = null,
    maxLimit: number = 12
  ) {
    // remove the oldest cookie if there are already 5 recently viewed items
    const MAX_RECENTLY_VIEWED_COOKIES = 5;
    this.manageRecentlyViewedCookies(cookieName, MAX_RECENTLY_VIEWED_COOKIES);

    let codes: string[] = [];
    let cookieStr: string | null = null;

    // normalize existingCookie to a string
    if (existingCookie) {
      cookieStr = Array.isArray(existingCookie) ? existingCookie[0] : existingCookie;
    }

    if (cookieStr) {
      try {
        // try parsing as JSON (new format)
        const parsed = JSON.parse(cookieStr);

        if (parsed.codes && typeof parsed.codes === 'string') {
          // new format: split space-separated string
          codes = parsed.codes.split(' ').filter(Boolean);
        } else {
          // parsed JSON but no codes key → treat as old format
          codes = decodeURIComponent(cookieStr).split(' ').filter(Boolean);

          // migrate old format to new JSON
          this.cookieService.put(
            cookieName,
            JSON.stringify({
              codes: codes.join(' '),
              updatedAt: Date.now(),
            })
          );
        }
      } catch {
        // old format: URL-encoded space-separated string → decode and split
        codes = decodeURIComponent(cookieStr).split(' ').filter(Boolean);

        this.cookieService.put(
          cookieName,
          JSON.stringify({
            codes: codes.join(' '),
            updatedAt: Date.now(),
          })
        );
      }
    }

    // add newValue at front, remove duplicates, limit
    codes = [newValue, ...codes.filter((c) => c !== newValue)].slice(0, maxLimit);

    // save updated cookie in new JSON format
    this.cookieService.put(
      cookieName,
      JSON.stringify({
        codes: codes.join(' '),
        updatedAt: Date.now(),
      })
    );
  }

  getCookie(key: string) {
    return this.cookieService.get(key);
  }
}
